"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import { api } from "@/lib/api";

export type PlayState = "idle" | "loading" | "playing" | "error";

function b64ToObjectUrl(data: string, contentType: string): string {
  const bin = atob(data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: contentType || "audio/wav" }));
}

/** Shared admin voice preview player (Cartesia stock/TTS + ElevenLabs preview). */
export function useCartesiaAudioPlayer() {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [state, setState] = useState<PlayState>("idle");
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlCacheRef = useRef<Map<string, string>>(new Map());

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.removeAttribute("src");
      a.load();
    }
    setActiveKey(null);
    setState("idle");
  }, []);

  useEffect(() => () => {
    stop();
    for (const url of urlCacheRef.current.values()) URL.revokeObjectURL(url);
    urlCacheRef.current.clear();
  }, [stop]);

  const playUrl = useCallback(async (key: string, url: string) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    a.src = url;
    a.onended = () => {
      setActiveKey(null);
      setState("idle");
    };
    a.onerror = () => {
      setActiveKey(null);
      setState("error");
      setError("Playback failed");
    };
    await a.play();
    setActiveKey(key);
    setState("playing");
  }, []);

  const playStockPreview = useCallback(async (voiceId: string) => {
    const key = `stock:${voiceId}`;
    if (activeKey === key && state === "playing") {
      stop();
      return;
    }
    stop();
    setState("loading");
    setActiveKey(key);
    setError(null);
    try {
      let url = urlCacheRef.current.get(key);
      if (!url) {
        const preview = await api.getCartesiaVoicePreview(voiceId);
        url = b64ToObjectUrl(preview.data, preview.contentType);
        urlCacheRef.current.set(key, url);
      }
      await playUrl(key, url);
    } catch (e) {
      setState("error");
      setActiveKey(null);
      setError(e instanceof Error ? e.message : "Preview failed");
    }
  }, [activeKey, playUrl, state, stop]);

  const playSynthesized = useCallback(async (
    key: string,
    opts: {
      transcript: string;
      voiceId?: string | null;
      speed?: number | null;
      volume?: number | null;
      emotion?: string | null;
    },
  ) => {
    if (activeKey === key && state === "playing") {
      stop();
      return;
    }
    stop();
    setState("loading");
    setActiveKey(key);
    setError(null);
    try {
      // Always fetch fresh — delivery settings / transcript can change.
      const audio = await api.synthesizeCartesiaSpeech({
        transcript: opts.transcript,
        voiceId: opts.voiceId,
        speed: opts.speed,
        volume: opts.volume,
        emotion: opts.emotion,
      });
      const url = b64ToObjectUrl(audio.data, audio.contentType);
      const prev = urlCacheRef.current.get(key);
      if (prev) URL.revokeObjectURL(prev);
      urlCacheRef.current.set(key, url);
      await playUrl(key, url);
    } catch (e) {
      setState("error");
      setActiveKey(null);
      setError(e instanceof Error ? e.message : "TTS failed");
    }
  }, [activeKey, playUrl, state, stop]);

  function stateFor(key: string): PlayState {
    if (activeKey !== key) return "idle";
    return state;
  }

  const playElevenLabsPreview = useCallback(async (voiceId: string) => {
    const key = `el:${voiceId}`;
    if (activeKey === key && state === "playing") {
      stop();
      return;
    }
    stop();
    setState("loading");
    setActiveKey(key);
    setError(null);
    try {
      let url = urlCacheRef.current.get(key);
      if (!url) {
        const preview = await api.getElevenLabsVoicePreview(voiceId);
        url = b64ToObjectUrl(preview.data, preview.contentType);
        urlCacheRef.current.set(key, url);
      }
      await playUrl(key, url);
    } catch (e) {
      setState("error");
      setActiveKey(null);
      setError(e instanceof Error ? e.message : "Preview failed");
    }
  }, [activeKey, playUrl, state, stop]);

  return {
    activeKey,
    state,
    error,
    stop,
    playStockPreview,
    playSynthesized,
    playElevenLabsPreview,
    stateFor,
  };
}

type PlayButtonProps = {
  playState: PlayState;
  onClick: (e?: React.MouseEvent) => void;
  variant?: "solid" | "outline" | "nudge";
  disabled?: boolean;
  label?: string;
  "aria-label"?: string;
};

export function VoicePlayButton({
  playState,
  onClick,
  variant = "outline",
  disabled,
  label,
  "aria-label": ariaLabel,
}: PlayButtonProps) {
  const busy = playState === "loading";
  const playing = playState === "playing";
  const failed = playState === "error";
  const filled = variant === "solid" || variant === "nudge";
  const icon = busy
    ? <Loader2 size={14} className="spin" color="currentColor" />
    : playing
      ? <Pause size={14} color="currentColor" fill={filled ? "currentColor" : "none"} />
      : <Play size={14} color="currentColor" fill={filled ? "currentColor" : "none"} />;

  return (
    <button
      type="button"
      className={`voice-play voice-play--${variant}${playing ? " is-playing" : ""}${busy ? " is-loading" : ""}${failed ? " is-error" : ""}`}
      disabled={disabled || busy}
      aria-label={ariaLabel || (playing ? "Pause preview" : busy ? "Loading preview" : "Play preview")}
      aria-busy={busy}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
      }}
    >
      {icon}
      {label ? <span>{playing ? "Pause" : busy ? "…" : label}</span> : null}
    </button>
  );
}
